import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Input, Label, Row } from 'reactstrap';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/dist/sweetalert2.min.css';
import 'sweetalert2/src/sweetalert2.scss';
import errorSound from '../../assets/audio/alert-error.wav';
import Breadcrumb from '../../components/common/breadcrumb';
import api from '../../services/api-configuration';
import { getMasterData } from '../devir-teslim/api';
import useFormatMessage from '../../utils/useFormatMessage';


const WarehouseEntryComponent = () => {
    const { t } = useFormatMessage();
    const [barcode, setBarcode] = useState("");
    const [error, setError] = useState("");
    const [lastEntry, setLastEntry] = useState();
    const [isProcessing, setIsProcessing] = useState(false);
    const inputRef = useRef(null);
    const [reasons, setReasons] = useState([]);
    useEffect(() => {
        getMasterData().then((md) => setReasons((md.refusal_reasons || []).filter((r) => r.is_active))).catch(() => setReasons([]));
    }, []);

    const validateChassisNumber = (value) => {
        if (!value) {
            setError("Şase numarası zorunludur");
            return false;
        }
        if (value.length !== 17) {
            setError("Şase numarası 17 karakter olmalıdır");
            return false;
        }
        setError("");
        return true;
    };

    const handleWarehouseEntry = async (chassis_no) => {
        if (!validateChassisNumber(chassis_no)) return;

        let body = {
            chassis_no: chassis_no.trim().toUpperCase(),
            warehouse_id: localStorage.getItem("warehouse")
                ? parseInt(Buffer.from(localStorage.getItem("warehouse"), 'base64').toString('ascii'))
                : null
        };

        try {
            const response = await api.post('/warehouseEntry', body);
            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.error || "İşlem sırasında bir hata oluştu"
            };
        }
    };

    const handleDealerReturn = async (chassis_no, reasonCode = '', note = '') => {
        let body = {
            chassis_no: chassis_no.trim().toUpperCase(),
            reason_code: reasonCode,
            note: note,
            warehouse_id: localStorage.getItem("warehouse")
                ? parseInt(Buffer.from(localStorage.getItem("warehouse"), 'base64').toString('ascii'))
                : null
        };

        try {
            const response = await api.post('/processDealerReturn', body);
            return response.data;
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.error || "İşlem sırasında bir hata oluştu"
            };
        }
    };

    const askDealerReturnReason = async () => {
        const inputOptions = reasons.reduce((options, reason) => {
            options[reason.code] = reason.name_tr;
            return options;
        }, {});
        const reasonResult = await Swal.fire({
            icon: 'warning',
            title: 'Bayi İadesi',
            text: 'Araç teslim edilemeden geri dönmüş. İade nedenini seçiniz.',
            input: 'select',
            inputOptions,
            inputPlaceholder: 'İade nedeni seçiniz',
            showCancelButton: true,
            confirmButtonText: 'Depoya Al',
            cancelButtonText: 'İptal',
            allowOutsideClick: false,
            inputValidator: (value) => !value ? 'İade nedeni zorunludur' : undefined
        });
        if (!reasonResult.isConfirmed) return null;

        let note = '';
        if (reasonResult.value === 'DIGER') {
            const noteResult = await Swal.fire({
                icon: 'info',
                title: 'İade Açıklaması',
                input: 'textarea',
                inputPlaceholder: 'İade nedenini açıklayınız',
                showCancelButton: true,
                confirmButtonText: 'Devam Et',
                cancelButtonText: 'İptal',
                allowOutsideClick: false,
                inputValidator: (value) => !value || !value.trim() ? 'Açıklama zorunludur' : undefined
            });
            if (!noteResult.isConfirmed) return null;
            note = noteResult.value.trim();
        }
        return { reasonCode: reasonResult.value, note };
    };

    // Tanımsız araç kontrolü için yeni fonksiyon
    const checkChassisDefinition = async (chassis_no) => {
        try {
            const response = await api.post("/getVehiclesByChassisPattern", {
                chassis_pattern: chassis_no.trim().toUpperCase()
            });

            if (response.data.success) {
                return {
                    isDefined: response.data.count > 0,
                    count: response.data.count
                };
            }
            return { isDefined: false, count: 0 };
        } catch (error) {
            console.error("Error checking chassis definition:", error);
            return { isDefined: false, count: 0 };
        }
    };

    // Tanımsız araç için onay alma fonksiyonu
    const showUndefinedChassisConfirmation = (chassis_no) => {
        // Hata sesi çal
        if (navigator.vibrate) {
            navigator.vibrate(200);
        }
        const errorAudio = document.getElementById("errorSound");
        if (errorAudio) {
            const playPromise = errorAudio.play();
            if (playPromise !== undefined) {
                playPromise.catch(err => console.log('Audio play failed:', err));
            }
        }

        const swalPromise = Swal.fire({
            icon: false,
            title: '',
            html: `
                <div style="text-align: left; padding: 0;">
                   
                    <div style="display: flex; align-items: center; margin-bottom: 20px;">
                       <i class="fa fa-exclamation-triangle" style="color: #e46a17; font-size: 20px;"></i>
                        <div style="font-size: 20px; font-weight: 700; color: #e46a17;">&nbsp;TANIMSIZ ARAÇ</div>
                    </div>
                    <div style="margin-bottom: 20px;">
                        <div style="font-size: 18px; font-weight: 600; color: #495057; margin-bottom: 15px; word-break: break-all; background-color: #f8f9fa; padding: 12px; text-align: center; border: 1px solid #dee2e6; border-radius: 6px; letter-spacing: 1px;">${chassis_no}</div>
                        <div style="font-size: 15px; color: #6c757d; line-height: 1.5;">
                            numaralı şasi sistemde tanımsızdır. <br />
                            Yine de giriş yapmak istiyor musunuz?
                        </div>
                    </div>
                    <div style="border-top: 1px solid #dee2e6; padding-top: 0px; margin-top: 0px;"></div>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Evet, Giriş Yap',
            confirmButtonColor: '#198754',
            confirmButtonTextColor: '#ffffff',

            cancelButtonText: 'Hayır',
            cancelButtonColor: '#6c757d',
            cancelButtonTextColor: '#ffffff',
            customClass: {
                popup: 'swal2-popup-large swal2-custom-line',
                confirmButton: 'swal2-confirm-button',
                cancelButton: 'swal2-cancel-button'
            },
            allowOutsideClick: false,
            allowEscapeKey: false,
            background: 'white'
        });

        // Modal açıldıktan hemen sonra focus'u kaldır
        setTimeout(() => {
            const confirmButton = document.querySelector('.swal2-confirm-button');
            if (confirmButton) {
                confirmButton.blur();
            }
        }, 50);

        return swalPromise;
    };

    const handleEntry = useCallback(async () => {
        // Eğer işlem devam ediyorsa yeni istek gönderme
        if (isProcessing) {
            return;
        }

        setIsProcessing(true);

        if (!validateChassisNumber(barcode)) {
            if (navigator.vibrate) {
                navigator.vibrate(200);
            }
            const errorAudio = document.getElementById("errorSound");
            if (errorAudio) {
                const playPromise = errorAudio.play();
                if (playPromise !== undefined) {
                    playPromise.catch(err => console.log('Audio play failed:', err));
                }
            }
            setIsProcessing(false);
            return;
        }

        try {
            // First try warehouse entry
            const warehouseResponse = await handleWarehouseEntry(barcode);

            if (warehouseResponse.success) {
                // If warehouse entry is successful
                const { new_zone, new_address } = warehouseResponse.data;
                const locationDisplay = `${new_zone} - ${new_address}`;

                // Save the chassis and its location
                setLastEntry({
                    chassis: barcode.trim().toUpperCase(),
                    location: locationDisplay,
                    type: 'warehouse'
                });

                Swal.fire({
                    icon: 'success',
                    title: 'Stok Konumu',
                    html: `<div style="font-size: 24px;">${locationDisplay}</div>`,
                    timer: 3000,
                    showConfirmButton: false,
                    position: 'center',
                    allowOutsideClick: false,
                    customClass: {
                        popup: 'swal2-popup-large'
                    }
                }).then(() => {
                    setTimeout(() => {
                        setBarcode('');
                        setError('');
                        setIsProcessing(false);
                        // İşlem tamamlandıktan sonra input'a focus ver
                        if (inputRef.current) {
                            inputRef.current.focus();
                        }
                    }, 500);
                });
            }
            else if (warehouseResponse.error?.includes("Bu şase farklı bir depoya transfer")) {
                // If warehouse entry is not successful and the error is "Bu şase farklı bir depoda bulunmaktadır"
                Swal.fire({
                    icon: 'error',
                    title: 'Hata',
                    html: `<div style="font-size: 24px;">${t(warehouseResponse.error)}</div>`,
                    showConfirmButton: true,
                    confirmButtonText: 'Tamam',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    background: '#fff5f5',
                    customClass: {
                        popup: 'swal2-popup-large'
                    }
                }).then(() => {
                    setTimeout(() => {
                        setBarcode('');
                        setError('');
                        setIsProcessing(false);
                        if (inputRef.current) {
                            inputRef.current.focus();
                        }
                    }, 500);
                });
            } else if (warehouseResponse.error?.includes("aktif bir") ||
                warehouseResponse.error?.includes("Depo giriş okutulmadan")) {
                // If warehouse entry is not successful and the error is "Bu şase farklı bir depoda bulunmaktadır"
                Swal.fire({
                    icon: 'error',
                    title: 'Hata',
                    html: `<div style="font-size: 24px;">${t(warehouseResponse.error)}</div>`,
                    showConfirmButton: true,
                    confirmButtonText: 'Tamam',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    background: '#fff5f5',
                    customClass: {
                        popup: 'swal2-popup-large'
                    }
                }).then(() => {
                    setBarcode('');
                    setError('');
                    setIsProcessing(false);
                    if (inputRef.current) {
                        inputRef.current.focus();
                    }
                });
            } else if (warehouseResponse.error?.includes("şase adresleme işleminde hata:")) {
                // If warehouse entry is not successful and the error is "şase adresleme işleminde hata:"
                Swal.fire({
                    icon: 'error',
                    title: 'Hata',
                    html: `<div style="font-size: 24px;">${t(warehouseResponse.error)}</div>`,
                    showConfirmButton: true,
                    confirmButtonText: 'Tamam',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    background: '#fff5f5',
                    customClass: {
                        popup: 'swal2-popup-large'
                    }
                }).then(() => {
                    setTimeout(() => {
                        setBarcode('');
                        setError('');
                        setIsProcessing(false);
                        if (inputRef.current) {
                            inputRef.current.focus();
                        }
                    }, 500);
                });
            }
            else {
                // Warehouse entry başarısız olduğunda, önce aracın tanımlı olup olmadığını kontrol et
                const chassisDefinition = await checkChassisDefinition(barcode);

                // Eğer araç tanımsız ise onay al
                if (!chassisDefinition.isDefined) {
                    const confirmation = await showUndefinedChassisConfirmation(barcode.trim().toUpperCase());

                    if (!confirmation.isConfirmed) {
                        // Kullanıcı iptal etti
                        setTimeout(() => {
                            setBarcode('');
                            setError('');
                            setIsProcessing(false);
                            // İptal sonrası input'a focus ver
                            if (inputRef.current) {
                                inputRef.current.focus();
                            }
                        }, 500);
                        return;
                    }
                }

                let returnDetails = { reasonCode: '', note: '' };
                if (chassisDefinition.isDefined) {
                    if (!warehouseResponse.error?.includes("şase aktif değil")) {
                        Swal.fire({
                            icon: 'error',
                            title: 'Hata',
                            html: `<div style="font-size: 24px;">${t(warehouseResponse.error || "Araç depo girişine uygun değil")}</div>`,
                            confirmButtonText: 'Tamam',
                            allowOutsideClick: false
                        });
                        setIsProcessing(false);
                        return;
                    }
                    returnDetails = await askDealerReturnReason();
                    if (!returnDetails) {
                        setIsProcessing(false);
                        if (inputRef.current) inputRef.current.focus();
                        return;
                    }
                }

                const dealerResponse = await handleDealerReturn(barcode, returnDetails.reasonCode, returnDetails.note);

                if (dealerResponse.success) {
                    const { address } = dealerResponse;

                    // Save the chassis and its location
                    setLastEntry({
                        chassis: barcode.trim().toUpperCase(),
                        location: address,
                        type: 'dealer',
                        caseNo: dealerResponse.case_no
                    });

                    Swal.fire({
                        icon: 'success',
                        title: dealerResponse.case_no ? 'Bayi İadesi – Konum' : 'Stok Konumu',
                        html: `<div style="font-size: 24px;"> ${address}</div>${dealerResponse.case_no ? `<div style="font-size: 16px; margin-top: 8px;">Bayi iadesi · iade kaydı ${dealerResponse.case_no}</div>` : ''}`,
                        timer: 3000,
                        showConfirmButton: false,
                        position: 'center',
                        allowOutsideClick: false,
                        customClass: {
                            popup: 'swal2-popup-large'
                        }
                    }).then(() => {
                        setTimeout(() => {
                            setBarcode('');
                            setError('');
                            setIsProcessing(false);
                            // İşlem tamamlandıktan sonra input'a focus ver
                            if (inputRef.current) {
                                inputRef.current.focus();
                            }
                        }, 500);
                    });
                } else {
                    if (navigator.vibrate) {
                        navigator.vibrate(500);
                    }
                    const errorAudio = document.getElementById("errorSound");
                    if (errorAudio) {
                        const playPromise = errorAudio.play();
                        if (playPromise !== undefined) {
                            playPromise.catch(err => console.log('Audio play failed:', err));
                        }
                    }

                    Swal.fire({
                        icon: 'error',
                        title: 'Hata',
                        html: `<div style="font-size: 24px;">${t(dealerResponse.error)}</div>`,
                        showConfirmButton: true,
                        confirmButtonText: 'Tamam',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        background: '#fff5f5',
                        customClass: {
                            popup: 'swal2-popup-large'
                        }
                    }).then(() => {
                        setTimeout(() => {
                            setBarcode('');
                            setError('');
                            setIsProcessing(false);
                            if (inputRef.current) {
                                inputRef.current.focus();
                            }
                        }, 500);
                    });
                }
            }
        } catch (error) {
            Swal.fire({
                title: '<div class="text-red-600">Hata</div>',
                html: '<div class="text-gray-700">İşlem sırasında bir hata oluştu</div>',
                icon: 'error',
                showCancelButton: true,
                timerProgressBar: true,
                showConfirmButton: false,
                allowOutsideClick: false,
            }).then(() => {
                setTimeout(() => {
                    setBarcode('');
                    setError('');
                    setIsProcessing(false);
                    if (inputRef.current) {
                        inputRef.current.focus();
                    }
                }, 500);
            });
        }
    }, [barcode, isProcessing, t, reasons]);

    const handleBarcodeChange = (e) => {
        const value = e.target.value;
        setBarcode(value.toUpperCase().trim());
        if (value.length > 0) {
            validateChassisNumber(value);
        } else {
            setError("");
        }
    };

    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter' && barcode.trim() && !isProcessing) {
            handleEntry();
        }
    }, [barcode, isProcessing, handleEntry]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyPress);

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleKeyPress]);

    return (
        <>
            <Breadcrumb
                current="Depo Giriş"
                depo={true}
            />
            <Card className="p-4">
                <Row>
                    <Col md="6">
                        <Label
                            className="mb-2 font-semibold"
                            style={{
                                fontWeight: "600"
                            }}
                        >
                            {t("Şase No")}
                        </Label>
                        <Input
                            type="text"
                            value={barcode}
                            onChange={handleBarcodeChange}
                            placeholder={t("17 haneli şase numarası giriniz")}
                            maxLength={17}
                            className={error ? "border-danger" : ""}
                            ref={inputRef}
                        />
                        {error && (
                            <div className="text-danger mt-1">
                                {error}
                            </div>
                        )}
                    </Col>
                </Row>
                <hr />
                <Row>
                    <Button
                        color='success'
                        className='ml-2 w-50'
                        onClick={handleEntry}
                        disabled={!!error || !barcode || isProcessing}
                    >
                        <i className={isProcessing ? 'fa fa-spinner fa-spin' : 'fa fa-check'}></i> &nbsp;
                        {isProcessing ? 'İşleniyor...' : t("Tamam")}
                    </Button>
                </Row>

                {lastEntry && (
                    <Row className="mt-4">
                        <Col sm="12" md="6">
                            <div className="p-3 border rounded bg-light">
                                <h6 className="mb-2 text-dark"> --- Son İşlem</h6>
                                <div className="mb-1 text-dark">
                                    <span className="font-weight-bold">Şase:</span> {lastEntry.chassis}
                                </div>
                                <div className="text-dark">
                                    <span className="font-weight-bold">Konum:</span> {lastEntry.location}
                                </div>
                                {lastEntry.caseNo && (
                                    <div className="text-dark">
                                        <span className="font-weight-bold">İade kaydı:</span> {lastEntry.caseNo}
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                )}
            </Card>
            <audio id="errorSound" src={errorSound} preload="auto"></audio>

            <style jsx>{`
                .swal2-custom-line {
                    border-top: 5px solid #e46a17 !important;
                }
                .swal2-cancel-button {
                    border: none !important;
                    background-color: #6c757d !important;
                    color: #ffffff !important;
                }
                .swal2-cancel-button:hover {
                    background-color: #5a6268 !important;
                }
                .swal2-confirm-button:focus,
                .swal2-cancel-button:focus {
                    outline: none !important;
                    box-shadow: none !important;
                }
            `}</style>
        </>
    );
};

export default WarehouseEntryComponent;
